'use client'

export const AUTH_ROUTES = {
  // Public auth routes
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  VERIFY_EMAIL: '/auth/verify-email',
  LOGOUT: '/auth/logout',
  ERROR: '/auth/error',

  // Unified dashboard route – all roles navigate here after login
  DASHBOARD: '/dashboard',

  // Super Admin routes
  SUPER_ADMIN: {
    LOGIN: '/super-admin/login',
    DASHBOARD: '/dashboard',
    USERS: '/dashboard/users',
    SYSTEM: '/dashboard/health/monitoring',
    AUDIT: '/dashboard/security/audit-logs',
    SETTINGS: '/dashboard/settings',
  },

  // Admin routes
  ADMIN: {
    LOGIN: '/admin/login',
    DASHBOARD: '/dashboard',
    USERS: '/dashboard/users',
    REPORTS: '/dashboard/reports',
    SETTINGS: '/dashboard/settings',
  },

  // Staff routes
  STAFF: {
    LOGIN: '/auth/login', // Staff uses general login
    DASHBOARD: '/dashboard',
    ENROLLMENT: '/dashboard/enrollment',
    BIOMETRICS: '/dashboard/biometrics',
    DOCUMENTS: '/dashboard/documents',
    REPORTS: '/dashboard/reports',
    SETTINGS: '/dashboard/settings',
  },

  // Citizen routes
  CITIZEN: {
    LOGIN: '/auth/login',
    DASHBOARD: '/dashboard',
    PROFILE: '/profile',
    DOCUMENTS: '/documents',
    SERVICES: '/services',
  },
}

/**
 * Get forgot password URL with proper return URL
 */
export function getForgotPasswordUrl(
  userType: 'super-admin' | 'admin' | 'staff' | 'citizen' = 'citizen'
) {
  const returnUrls = {
    'super-admin': AUTH_ROUTES.SUPER_ADMIN.LOGIN,
    admin: AUTH_ROUTES.ADMIN.LOGIN,
    staff: AUTH_ROUTES.STAFF.LOGIN,
    citizen: AUTH_ROUTES.CITIZEN.LOGIN,
  }

  return `${AUTH_ROUTES.FORGOT_PASSWORD}?type=${userType}&returnUrl=${encodeURIComponent(
    returnUrls[userType]
  )}`
}

/**
 * Get dashboard URL based on user role – all roles use unified /dashboard
 */
export function getDashboardUrl(role: string): string {
  return AUTH_ROUTES.DASHBOARD
}

/**
 * Get login URL based on user type
 */
export function getLoginUrl(
  userType: 'super-admin' | 'admin' | 'staff' | 'citizen' = 'citizen'
): string {
  const loginUrls = {
    'super-admin': AUTH_ROUTES.SUPER_ADMIN.LOGIN,
    admin: AUTH_ROUTES.ADMIN.LOGIN,
    staff: AUTH_ROUTES.STAFF.LOGIN,
    citizen: AUTH_ROUTES.CITIZEN.LOGIN,
  }

  return loginUrls[userType]
}

/**
 * Check if a path is an authentication route
 */
export function isAuthRoute(pathname: string): boolean {
  const authPaths = getAllAuthRoutes()
  // 🔑 exact match only (no startsWith to avoid overlap like /super-admin/login → /login)
  return authPaths.includes(pathname)
}

/**
 * Check if a path is a public route (no auth required)
 */
export function isPublicRoute(pathname: string): boolean {
  const publicPaths = [
    '/',
    '/about',
    '/contact',
    '/help',
    '/privacy',
    '/terms',
    ...getAllAuthRoutes(),
  ]

  return publicPaths.includes(pathname)
}

export function getAllAuthRoutes(): string[] {
  return [
    AUTH_ROUTES.LOGIN,
    AUTH_ROUTES.REGISTER,
    AUTH_ROUTES.FORGOT_PASSWORD,
    AUTH_ROUTES.RESET_PASSWORD,
    AUTH_ROUTES.VERIFY_EMAIL,
    AUTH_ROUTES.LOGOUT,
    AUTH_ROUTES.ERROR,
    AUTH_ROUTES.SUPER_ADMIN.LOGIN,
    AUTH_ROUTES.ADMIN.LOGIN,
    AUTH_ROUTES.STAFF.LOGIN,
    AUTH_ROUTES.CITIZEN.LOGIN,
  ]
}

export function getAllPublicRoutes(): string[] {
  return ['/', '/about', '/contact', '/help', '/privacy', '/terms', ...getAllAuthRoutes()]
}

/**
 * Route protection configuration
 */
export const ROUTE_PROTECTION = {
  '/super-admin': {
    requiredRoles: ['SUPER_ADMIN'],
    require2FA: true,
  },
  '/admin': {
    requiredRoles: ['SUPER_ADMIN', 'ADMIN', 'REGIONAL_ADMIN', 'WOREDA_ADMIN', 'KEBELE_ADMIN'],
    require2FA: true,
  },
  '/staff': {
    requiredRoles: [
      'STAFF',
      'ENROLLMENT_OFFICER',
      'DATA_ENTRY_CLERK',
      'SUPERVISOR',
      'MANAGER',
      'ADMIN',
      'SUPER_ADMIN',
    ],
    require2FA: false,
  },
  '/dashboard': {
    requiredRoles: ['CITIZEN', 'STAFF', 'ADMIN', 'SUPER_ADMIN'],
    require2FA: false,
  },
}
